// How a vendor's product list filters: by what kind of thing a product is (the
// profile its type takes, not the store's purchase type) and by where it stands in
// review. The browser's own purchase-type and platform filters are dropped
// for them (app store links are a store matter).
import React, { useEffect, useMemo, useState } from "react"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { useContentTypes } from "../Store/extensions/shared/content-types"
import { useProductTypes, productTypeQuery } from "../AdminShared/product-types/useProductTypes"
import { PUBLISH_STATUS } from "../AdminShared/ui"
import { OrgProductTypes } from "../AdminShared/api"
import usePortalTexts from "./texts/usePortalTexts"
import { PRODUCTS } from "./texts/bundles"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

/** Filters for the store products browser in a vendor's views; `orgId`: whose products — the type filter offers their kinds only. */
export default function useVendorProductFilters(orgId) {
  const contentTypes = useContentTypes()
  const { list: typeTree } = useProductTypes()
  const t = usePortalTexts(PRODUCTS)
  // the kinds of product the organization has (null while they load, or with no organization: all of them)
  const [own, setOwn] = useState(null)
  useEffect(() => {
    setOwn(null)
    if (!orgId) return
    let alive = true
    OrgProductTypes(orgId)
      .then((res) => alive && Array.isArray(res?.profiles) && setOwn(res.profiles))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [orgId])
  return useMemo(() => {
    const offered = own ? contentTypes.filter((type) => own.includes(type.contentId)) : contentTypes
    const typeOptions = offered.map((type) => ({ value: type.contentId, name: type.name || type.contentId }))
    const statusOptions = PUBLISH_STATUS.map((status) => ({ value: status.value, name: t(status.label) }))
    const valuesOf = (selected) => (selected || []).map((option) => option.value)

    return {
      hiddenFilters: ["type", "platform"],
      filters: [
        {
          id: "contentType",
          render: ({ value, onChange }) => (
            <DropDownSelect
              variant="admin"
              options={typeOptions}
              multiSelection
              fixedWidth="180px"
              placeholder={t`Filter by type`}
              searchEnabled
              value={value || []}
              onChange={onChange}
              showCheckbox={false}
              showSelectionTick={true}
              onItemRender={(option) => <span className="text-sm text-admin_text">{option.name}</span>}
              // the trigger: the one type chosen, by name
              onValueItemRender={(option) => (option ? <span className="text-sm">{option.name}</span> : t`Filter by type`)}
            />
          ),
          // a product names only its type: the types whose profile is one of those chosen
          match: (value) => (value?.length ? productTypeQuery(typeTree, valuesOf(value)) : null),
        },
        {
          id: "publishStatus",
          render: ({ value, onChange }) => (
            <span className="inline-flex items-center gap-1">
            <DropDownSelect
              variant="admin"
              options={statusOptions}
              multiSelection
              fixedWidth="180px"
              placeholder={t`Filter by status`}
              fitOptions
              value={value || []}
              onChange={onChange}
              showCheckbox={false}
              showSelectionTick={true}
            />
            {/* the help center on finding a product, and on the states the filter names */}
            <HelpHint helpKey="vendor.products#search" />
            </span>
          ),
          // a product never sent for review may have no status yet: a Draft
          match: (value) => {
            if (!value?.length) return null
            const statuses = valuesOf(value)
            return { publishStatus: { $in: statuses.includes(0) ? [...statuses, null] : statuses } }
          },
        },
      ],
    }
  }, [contentTypes, typeTree, t, own])
}
